/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { IFilterField } from "Interfaces/IFilterField";
import { IKeyText } from "Interfaces/IKeyText";

import { fieldTypes } from "./FilterFields";
import { RolesEnum, RoleNamesEnum } from "./RolesPermissionsConstants";
import { TenantTypesEnum } from "./TenantTypes";

export const defaultListingPageSize = 16;

export const accessControlUserStatusTypes = {
  ACTIVE: "Active",
  INACTIVE: "In-Active",
};

/**
 * Values for status dropdown
 * in sidebar filtering on access control page.
 */
export const accessControlUserStatus: IKeyText[] = [
  {
    key: "true",
    text: accessControlUserStatusTypes.ACTIVE,
  },
  {
    key: "false",
    text: accessControlUserStatusTypes.INACTIVE,
  },
];

export const accessControlMultiple = "Multiple";

/**
 * Values for role dropdown
 * in sidebar filtering on access control page.
 */
export const accessControlUserRole = {
  MVP: [
    {
      key: RolesEnum.MVP_PROGRAM_OWNER,
      text: RoleNamesEnum.PROGRAM_OWNER,
      disabled: true,
    },
    {
      key: RolesEnum.MVP_REVIEWER,
      text: RoleNamesEnum.COMMUNITY_MANAGER,
    },
    {
      key: RolesEnum.MVP_TECH_AREA_VALIDATOR,
      text: RoleNamesEnum.PRODUCT_GROUP_PARTNER,
    },
    {
      key: RolesEnum.MVP_BENEFITS_MANAGER,
      text: RoleNamesEnum.BENEFITS_MANAGER,
    },
    {
      key: RolesEnum.MVP_EXECUTIVE,
      text: RoleNamesEnum.EXECUTIVE,
    },
    {
      key: RolesEnum.MVP_BUSINESS_GROUP,
      text: RoleNamesEnum.BUSINESS_GROUP,
    },
  ],
  MLSA: [
    {
      key: RolesEnum.MSA_PROGRAM_OWNER,
      text: RoleNamesEnum.PROGRAM_OWNER,
      disabled: true,
    },
    {
      key: RolesEnum.MSA_REVIEWER,
      text: RoleNamesEnum.COMMUNITY_MANAGER,
    },
    {
      key: RolesEnum.MSA_GUEST,
      text: RoleNamesEnum.GUEST,
    },
    {
      key: RolesEnum.MSA_BENEFITS_MANAGER,
      text: RoleNamesEnum.BENEFITS_MANAGER,
    },
  ],
  RD: [
    {
      key: RolesEnum.RD_PROGRAM_OWNER,
      text: RoleNamesEnum.PROGRAM_OWNER,
      disabled: true,
    },
    {
      key: RolesEnum.RD_REVIEWER,
      text: RoleNamesEnum.COMMUNITY_MANAGER,
    },
    {
      key: RolesEnum.RD_GUEST,
      text: RoleNamesEnum.GUEST,
    },
    {
      key: RolesEnum.RD_BENEFITS_MANAGER,
      text: RoleNamesEnum.BENEFITS_MANAGER,
    },
  ],
};

/**
 * TODO: This will come from backend.
 * Values for role dropdown
 * in sidebar filtering on access control page.
 */
export const accessControlRegions: IKeyText[] = [
  {
    key: "United States",
    text: "United States",
  },
];

/**
 * Default field strings
 * we use to set in reducer.
 * Also used in sidebar filtering.
 */
export const USER_STATUS = "userStatus";

export const COUNTRY_REGION = "country";

export const STATE_PROVINCE = "stateProvince";

export const ROLE = "userRole";

export const REGION = "region";

export const TECHNOLOGY_AREA = "technologyarea";

/**
 * Constants used in the left side filtering menu
 * on the Access Control page for MLSA program.
 */
export const mlsaAccessControlFilterFields: IFilterField[] = [
  {
    key: USER_STATUS,
    text: "Status",
    type: fieldTypes.MULTI_SELECT,
    options: accessControlUserStatus,
  },
  {
    key: ROLE,
    text: "Roles",
    type: fieldTypes.MULTI_SELECT,
    options: accessControlUserRole[TenantTypesEnum.MLSA],
  },
  {
    key: REGION,
    text: "Region",
    type: fieldTypes.CL_REGION,
  },
  {
    key: COUNTRY_REGION,
    text: "Country",
    type: fieldTypes.COUNTRY,
  },
  {
    key: STATE_PROVINCE,
    text: "State/Province",
    type: fieldTypes.PROVINCE,
  },
];

/**
 * Constants used in the left side filtering menu
 * on the Access Control page for MVP program.
 */
export const mvpAccessControlFilterFields: IFilterField[] = [
  {
    key: USER_STATUS,
    text: "Status",
    type: fieldTypes.MULTI_SELECT,
    options: accessControlUserStatus,
  },
  {
    key: ROLE,
    text: "Roles",
    type: fieldTypes.MULTI_SELECT,
    options: accessControlUserRole[TenantTypesEnum.MVP],
  },
  {
    key: REGION,
    text: "Region",
    type: fieldTypes.CL_REGION,
  },
  {
    key: COUNTRY_REGION,
    text: "Country",
    type: fieldTypes.COUNTRY,
  },
  {
    key: STATE_PROVINCE,
    text: "State/Province",
    type: fieldTypes.PROVINCE,
  },
  {
    key: TECHNOLOGY_AREA,
    text: "Technology Areas",
    type: fieldTypes.TECHNOLOGY_AREA,
  },
];

/**
 * Constants used in the left side filtering menu
 * on the Access Control page for RD program.
 */
export const rdAccessControlFilterFields: IFilterField[] = [
  {
    key: USER_STATUS,
    text: "Status",
    type: fieldTypes.MULTI_SELECT,
    options: accessControlUserStatus,
  },
  {
    key: ROLE,
    text: "Roles",
    type: fieldTypes.MULTI_SELECT,
    options: accessControlUserRole[TenantTypesEnum.RD],
  },
  {
    key: REGION,
    text: "Region",
    type: fieldTypes.CL_REGION,
  },
  {
    key: COUNTRY_REGION,
    text: "Country",
    type: fieldTypes.COUNTRY,
  },
  {
    key: STATE_PROVINCE,
    text: "State/Province",
    type: fieldTypes.PROVINCE,
  },
];

/**
 * Default filter values.
 */
export const defaultFilterValues: {
  fieldName: string;
  value: string;
}[] = [];
